<template>
  <v-layout row wrap>
    <v-flex xs12>
      <v-card class="max-shadow">
        <v-card-title class="pa-1">
          <v-toolbar flat color="white">
            <v-toolbar-title>Invite Log</v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <datePicker v-model="selectedMonth" :max="maxMonth" type="month" />
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>
        <div class="px-3">
          <v-progress-linear :active="loading" indeterminate height="2" top></v-progress-linear>
        </div>
        <v-card-text>
          <barChart :chart-data="chartData" :options="chartOptions" :height="chartHeight" />
        </v-card-text>
        <v-card-title class="pa-1">
          <v-toolbar flat color="white">
            <v-toolbar-title>Employee Invite Log</v-toolbar-title>
          </v-toolbar>
        </v-card-title>
        <v-card-text>
          <barChart :chart-data="employeeChartData" :options="chartOptions" :height="chartHeight" />
        </v-card-text>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import datePicker from '@/components/common/DatePicker';
import * as moment from 'moment';
import barChart from '@/components/common/BarChart';
import _get from 'lodash/get';
import _times from 'lodash/times';
import { database } from '@/firebase';

export default {
  name: 'InviteLogChart',
  components: {
    datePicker,
    barChart
  },
  data() {
    return {
      dateFormat: 'YYYY-MM',
      selectedMonth: '',
      maxMonth: '',
      startAt: '',
      endAt: '',
      inviteLog: [],
      chartData: null,
      chartHeight: 150,
      chartOptions: null,
      yStepSize: 0.25,
      employeeChartData: null,
      firebaseListeners: [],
      loading: true
    };
  },
  computed: {
    logRef() {
      const lid = _get(this.$store.state.VMS, 'lid');
      const role = _get(this.$store.state.profile, 'role');
      const cid = _get(this.$store.state.profile, 'cid');
      // return null if the location ID is not yet set
      if (!lid) return null;
      if (role === 'admin')
        return database.ref(`vms/${lid}/tenants_data/${cid}/invites`);

      return database.ref(`vms/${lid}/invites`);
    }
  },
  watch: {
    logRef: {
      handler(val) {
        if (!val) return;

        this.firebaseListeners.forEach(listenerFunc => {
          val.off('value', listenerFunc);
        });

        this.fetchLog();
      },
      immediate: true
    },
    selectedMonth: {
      handler(val) {
        if (!val) return;
        this.startAt = moment
          .utc(val)
          .startOf('month')
          .toISOString();
        this.endAt = moment
          .utc(val)
          .endOf('month')
          .toISOString();
        if (!this.logRef) return;

        this.firebaseListeners.forEach(listenerFunc => {
          this.logRef.off('value', listenerFunc);
        });

        this.fetchLog();
      }
    },
    inviteLog: {
      handler(val) {
        if (!val) return;
        this.inviteLogChanged();
      },
      immediate: true
    }
  },
  created() {
    const date = moment();
    this.startAt = date.startOf('month').toISOString();
    this.endAt = date.endOf('month').toISOString();
    this.selectedMonth = date.format(this.dateFormat);
    this.maxMonth = date.format(this.dateFormat);
    // console.log(this.maxMonth);
    // console.log(this.startAt, this.endAt);
  },
  beforeDestroy() {
    this.firebaseListeners.forEach(listenerFunc => {
      this.logRef.off('value', listenerFunc);
    });
  },
  methods: {
    fetchLog() {
      this.loading = true;
      const log = this.logRef
        .orderByChild('expectedAt')
        .startAt(this.startAt)
        .endAt(this.endAt)
        .on('value', inviteLogSnapshot => {
          if (inviteLogSnapshot) {
            const log = inviteLogSnapshot.val();
            const inviteLogArray = this._toArray(log);
            this.inviteLog = inviteLogArray;
          }
        });
      this.firebaseListeners.push(log);
    },
    _toArray(obj) {
      if (!obj) return [];
      return Object.keys(obj).map(key => {
        const item = obj[key];
        item.id = key;
        return item;
      });
    },

    inviteLogChanged() {
      const data = this.inviteLog;
      const chartData = {};
      const employeeInvites = {};
      data.forEach(log => {
        const date = moment(log.expectedAt)
          .startOf('day')
          .format('DD MMM');
        const val = chartData[date];
        chartData[date] = val ? val + 1 : 1;

        if (!employeeInvites[log.host.uid]) {
          employeeInvites[log.host.uid] = { name: log.name, count: 0 };
        }
        employeeInvites[log.host.uid].count += 1;
      });
      // create x axis

      const daysInMonth = [];
      const monthDate = moment.utc(this.selectedMonth).startOf('month');
      _times(monthDate.daysInMonth(), () => {
        daysInMonth.push(monthDate.format('DD MMM'));
        monthDate.add(1, 'day');
      });
      // create dates array - data
      const dates = [];
      let pauseIndex = 0;
      Object.keys(chartData).forEach((objKey, objIndex) => {
        // TODO: Code Optimization
        daysInMonth.some((key, index) => {
          const date = moment(objKey, 'DD MMM').diff(
            moment(key, 'DD MMM'),
            'days'
          );
          if (objIndex === 0) {
            if (date === 0) {
              dates.push(chartData[key]);
              pauseIndex = index + 1;
              return date === 0;
            }
            dates.push(0);
            return false;
          } else if (objIndex !== 0) {
            if (pauseIndex <= index) {
              if (date === 0) {
                dates.push(chartData[key]);
                pauseIndex = index + 1;
                return date === 0;
              }
              dates.push(0);
              return false;
            }
            return false;
          }
          return false;
        });
      });
      if (daysInMonth.length > pauseIndex) {
        daysInMonth.forEach((key, index) => {
          if (pauseIndex <= index) {
            dates.push(0);
          }
        });
      }
      // option object
      this.chartData = {
        datasets: [
          {
            label: 'Invite Count',
            backgroundColor: '#3366cc',
            data: dates
          }
        ],
        labels: daysInMonth
      };

      var log = [];
      for (var val of Object.values(employeeInvites)) {
        log.push([val.name, val.count]);
      }

      log.sort(function(a, b) {
        return b[1] - a[1];
      });

      while (log.length < 10) {
        log.push(['', 0]);
      }
      log = log.slice(0, 20);

      this.employeeChartData = {
        datasets: [
          {
            label: 'Invite Count',
            backgroundColor: '#3366cc',
            data: log.map(val => val[1])
          }
        ],
        labels: log.map(val => val[0])
      };
      this.chartOptions = {
        responsive: true,
        legend: false,
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true
              },
              gridLines: {
                display: false
              }
            }
          ],
          xAxes: [
            {
              gridLines: {
                display: false
              }
            }
          ]
        }
      };
      this.loading = false;
    }
  }
};
</script>
